import { useEffect, useState } from 'react'
import { format, getMonth, subDays } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import { enGB } from 'react-day-picker/locale'
import 'react-day-picker/style.css'

import styles from './DatePicker.module.scss'
import { WeekdaysMileage } from './components/Weekdays'
import { NextMonthButton } from './components/NextMonthButton'
import { PreviousMonthButton } from './components/PreviousMonthButton'
import stylesMileage from './DatePickerMileage.module.scss'
import { useGetMileagePeriod } from '@/modules/CombinedLogs'
import ChevronRightIcon from '../assets/chevron-right.svg'
import LockIcon from '../assets/lock.svg'
import DangerFlagIcon from '@/modules/CombinedLogs/assets/danger-flag.svg'
import FlagIcon from '@/modules/CombinedLogs/assets/flag.svg'
import CircleIcon from '../assets/circle.svg'
import clsx from 'clsx'

interface Props {
    onChange?: (date: Date) => void
    value?: Date
    className?: string
}

export const DatePickerMileage = ({ value = new Date(), onChange, className }: Props) => {
    const [selectedMonth, setSelectedMonth] = useState(value)
    const { data: periods } = useGetMileagePeriod()

    const handleDateChange = (selected: Date) => onChange && onChange(selected)
    const handleSelectedMonthChange = (selected: Date) => setSelectedMonth(selected)

    useEffect(() => {
        if (getMonth(value) !== getMonth(selectedMonth)) {
            setSelectedMonth(value)
        }
    }, [value])

    const today = new Date()
    const isFriday = today.getDay() === 5

    const handlePeriodClick = (date: string) => {
        onChange && onChange(new Date(date))
    }

    return (
        <div className={stylesMileage.rootMileage}>
            <div className={stylesMileage.periodsContainer}>
                <div className={stylesMileage.title}>Mileage period</div>

                <div>
                    {periods?.periods.map((period, index) => (
                        <button
                            className={stylesMileage.period}
                            key={period.date}
                            onClick={() => handlePeriodClick(period.date)}
                            disabled={period.summary.isEmpty || period.summary.isLocked || (isFriday && index === 0)}
                        >
                            <div
                                className={clsx(stylesMileage.periodDate, {
                                    [stylesMileage.periodDateFriday]:
                                        (isFriday && index === 0) || period.summary.isEmpty,
                                })}
                            >
                                {period.dayOfWeek} {format(period.date, 'dd')}
                                {period.summary.isLocked && <LockIcon />}
                            </div>
                            <div
                                className={clsx(stylesMileage.periodInfo, {
                                    [stylesMileage.periodInfoFriday]:
                                        (isFriday && index === 0) || period.summary.isEmpty,
                                })}
                            >
                                {!(isFriday && index === 0) && period.summary.activeFlagCount > 0 && (
                                    <span className={stylesMileage.periodCount}>
                                        <DangerFlagIcon />
                                        {period.summary.activeFlagCount}
                                    </span>
                                )}
                                {!(isFriday && index === 0) && period.summary.doneFlagCount > 0 && (
                                    <span className={clsx(stylesMileage.periodCount, stylesMileage.periodCountDone)}>
                                        <FlagIcon />
                                        {period.summary.doneFlagCount}
                                    </span>
                                )}
                                {!(isFriday && index === 0) && period.summary.unfinishedCount > 0 && (
                                    <span className={stylesMileage.periodCount}>
                                        <CircleIcon />
                                        {period.summary.unfinishedCount}
                                    </span>
                                )}
                                <ChevronRightIcon />
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <DayPicker
                required
                onSelect={handleDateChange}
                onMonthChange={handleSelectedMonthChange}
                mode="single"
                animate
                weekStartsOn={6}
                showOutsideDays
                selected={value}
                month={selectedMonth}
                locale={enGB}
                disabled={{ after: subDays(new Date(), 1) }}
                className={clsx(stylesMileage.datePickerContainer, className)}
                classNames={{
                    caption_label: styles.label,
                    day: styles.day,
                    day_button: styles.dayButton,
                    outside: styles.outside,
                    nav: styles.nav,
                    month_caption: styles.caption,
                    selected: styles.daySelected,
                    today: styles.today,
                    disabled: styles.outside,
                }}
                components={{
                    NextMonthButton: () => <NextMonthButton selectedMonth={selectedMonth} />,
                    PreviousMonthButton,
                    Weekdays: WeekdaysMileage,
                }}
            />
        </div>
    )
}
